/** @jsxImportSource @emotion/react */
"use client";
import { type Interpolation } from "@emotion/react";
import { I18nMessage, Link, useTranslations } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import {
  type CommunityBroadcast,
  getCommunityMemberCount,
} from "@packages/sdk";
import type { CSSColorStrict, ThemeType } from "@packages/ui";
import {
  ArrowLeftIcon,
  AvatarCommunity,
  CloseIcon,
  colors,
  cssMediaQueryMinWidthTablet,
  FullscreenModalModal,
  IconButton,
  ListProfile,
  numericPercentages,
  radius,
  spacing,
  Text,
  useIsDesktopViewport,
  zIndices,
} from "@packages/ui";
import * as Dialog from "@radix-ui/react-dialog";

import { getBasePADUrl } from "../../../lib";

const communitiesStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.xxs,
  paddingBottom: spacing.m,
};

const contentStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  backgroundColor: theme.semanticColors.onOverlayBackground,
  borderRadius: radius.l,
  display: "flex",
  flexDirection: "column",
  left: numericPercentages[50],
  margin: spacing.m,
  maxWidth: 400,
  position: "fixed",
  top: numericPercentages[50],
  transform: "translate(-50%, -50%)",
  width: spacing.full,
  zIndex: zIndices.modalContent,
});

const headerStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.s,
  padding: spacing.m,
};

const overlayStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  backgroundColor: theme.semanticColors.higherContrastMed,
  inset: spacing.none,
  position: "fixed",
  zIndex: zIndices.overlayOverMediaPlayer,
});

const viewportStyles: Interpolation = {
  padding: spacing.none,
  height: `100dvh`,
  maxHeight: spacing.full,
  [cssMediaQueryMinWidthTablet]: {
    height: "auto",
    maxHeight: "400px",
  },
};

const titleStyles: Interpolation = {
  flex: 1,
  textAlign: "center",
};

const triggerStyles: Interpolation = {
  backgroundColor: colors.transparent,
  border: "none",
  cursor: "pointer",
  padding: 0,
};

const triggerTextStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  color: theme.semanticColors.primary,
});

type SentToModalProps = Dialog.DialogProps & {
  communityId: number;
  data: CommunityBroadcast;
};

export const SentToModal = ({
  communityId,
  data,
  ...props
}: SentToModalProps) => {
  const { track } = useAnalytics();
  const t = useTranslations();
  const isDesktop = useIsDesktopViewport();

  const handleTrigger = () =>
    track({
      event: HallowAnalyticsEvent.TappedSentBroadcastMessageGroups,
    });

  return (
    <FullscreenModalModal
      trigger={
        <Text
          size="xl"
          css={[triggerTextStyles, triggerStyles]}
          type="detail"
          onPointerDown={handleTrigger}
        >
          <strong>
            <I18nMessage
              message="dashboard_broadcast_groups"
              values={{
                dashboard_broadcast_groups:
                  data.broadcasted_to_communities?.length,
              }}
            />
          </strong>
        </Text>
      }
      modalProps={{
        contentCSS: [contentStyles],
        overlayCSS: [overlayStyles],
      }}
      fullScreenModalProps={{
        showCloseButton: false,
      }}
      scrollAreaProps={{ viewportCSS: viewportStyles }}
      {...props}
    >
      <div css={headerStyles}>
        {!isDesktop && (
          <Dialog.Close asChild>
            <IconButton
              icon={<ArrowLeftIcon />}
              size="s"
              variant="transparentPrimary"
            />
          </Dialog.Close>
        )}
        <Dialog.Title asChild>
          <Text size="m" css={titleStyles} type="title">
            <I18nMessage message="community_detail_groups_screen_title" />
          </Text>
        </Dialog.Title>
        {isDesktop && (
          <Dialog.Close asChild>
            <IconButton
              icon={<CloseIcon />}
              size="s"
              variant="transparentPrimary"
            />
          </Dialog.Close>
        )}
      </div>
      <div css={communitiesStyles}>
        {data.broadcasted_to_communities?.map((community) => (
          <ListProfile
            asChild
            avatar={
              <AvatarCommunity
                color={community.color_hex as CSSColorStrict}
                imgSrc={community.image_url}
                key="avatar"
                name={community.name}
                size="m"
              />
            }
            description={community.simple_address_label}
            icon={null}
            key={community.id}
            metadata={t("create_community_content_member_count", {
              create_community_content_member_count: getCommunityMemberCount({
                community,
              }),
            })}
            title={community.name}
          >
            <Link
              href={`${getBasePADUrl({ communityId })}/groups/${community.id}`}
            />
          </ListProfile>
        ))}
      </div>
    </FullscreenModalModal>
  );
};
